import { CompanyShareReportComponent } from '../company-share-report/company-share-report.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpService } from 'src/app/http.service';

interface SummaryColumn {
  key: string;
  title: string;
}

@Component({
  selector: 'app-consolidated-summary-report',
  standalone: true,
  imports: [CommonModule, FormsModule, CompanyShareReportComponent],
  templateUrl: './consolidated-summary-report.component.html',
  styleUrls: [
    '../dashboard/dashboard.component.scss',
    '../consolidated-dashboard/consolidated-dashboard.component.scss',
    './consolidated-summary-report.component.scss',
  ],
})
export class ConsolidatedSummaryReportComponent implements OnInit {
  public period = this.formatPeriod(new Date());
  public companies: any[] = [];
  public summaries: any[] = [];
  public startPeriod = '';
  public shareholder = false;
  public parentCompany: any = {};
  public is_loading = false;
  public error = '';
  public saving = false;
  public success = '';
  private requestId = 0;

  public columns: SummaryColumn[] = [
    { key: 'capital', title: 'Capital 本金' },
    { key: 'expenses', title: 'Spend' },
    { key: 'profit', title: 'Profit' },
    { key: 'net_profit', title: 'Net Profit' },
    { key: 'cash_bf', title: 'Last Month Cash' },
    { key: 'cash', title: 'Cash' },
  ];

  constructor(private http: HttpService) {}

  ngOnInit(): void {
    void this.load();
  }

  async load() {
    const currentRequest = ++this.requestId;
    this.is_loading = true;
    this.error = '';
    const response: any = await this.http.post('/admin/consolidated-summary-report/get', { period: this.period });
    if (currentRequest !== this.requestId) return;

    if (!response?.status) {
      this.companies = [];
      this.summaries = [];
      this.error = response?.message || 'Unable to load consolidated summary report.';
    } else {
      this.startPeriod = response.start_period || '';
      this.shareholder = response.shareholder === true;
      this.companies = response.companies || [];
      this.summaries = response.summaries || [];
      for (const summary of this.summaries) {
        if (summary.capital_edit) summary.capital_edit.draft_capital = summary.capital_edit.capital;
        if (summary.cash_edit) summary.cash_edit.draft_cash_bf = summary.cash_edit.cash_bf;
      }
      this.parentCompany = response.parent_company || {};
    }
    this.is_loading = false;
  }

  capitalAmount(row: any): number {
    return this.number(row.capital_amount);
  }

  async saveCapital(row: any) {
    if (this.saving || this.is_loading || !row.can_edit_capital) return;
    this.error = '';
    this.success = '';
    if (row.draft_capital === null || row.draft_capital === '' ||
        !/^-?\d+(\.\d{1,2})?$/.test(String(row.draft_capital))) {
      this.error = 'Enter a capital amount with at most two decimal places.';
      return;
    }
    this.saving = true;
    try {
      const response: any = await this.http.post('/admin/consolidated-summary-report/capital/save', {
        scope: row.scope,
        expected_revision: row.revision,
        period: row.period,
        capital: row.draft_capital,
        expected_capital: row.capital,
      });
      if (!response?.status) {
        this.error = response?.message || 'Unable to save capital.';
        return;
      }
      await this.load();
      if (!this.error) this.success = `Consolidated capital saved (${row.period}).`;
    } catch {
      this.error = 'Unable to save capital. Reload the report to check the saved amount before retrying.';
    } finally {
      this.saving = false;
    }
  }

  async saveCash(row: any) {
    if (this.saving || this.is_loading || !row.can_edit_cash) return;
    this.error = '';
    this.success = '';
    if (row.draft_cash_bf === null || row.draft_cash_bf === '' ||
        !/^-?\d+(\.\d{1,2})?$/.test(String(row.draft_cash_bf))) {
      this.error = 'Enter a cash B/F amount with at most two decimal places.';
      return;
    }
    this.saving = true;
    try {
      const response: any = await this.http.post('/admin/consolidated-summary-report/cash/save', {
        scope: row.scope,
        expected_revision: row.revision,
        period: row.period,
        cash_bf: row.draft_cash_bf,
        expected_cash_bf: row.cash_bf,
      });
      if (!response?.status) {
        this.error = response?.message || 'Unable to save cash B/F.';
        return;
      }
      await this.load();
      if (!this.error) this.success = `Consolidated cash B/F saved (${row.period}).`;
    } catch {
      this.error = 'Unable to save cash B/F. Reload the report to check the saved amount before retrying.';
    } finally {
      this.saving = false;
    }
  }

  currencyLabel(currency: string): string {
    return currency && currency !== 'none' ? currency : '—';
  }

  private number(value: unknown): number {
    const parsed = parseFloat(String(value ?? 0));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatPeriod(date: Date): string {
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2);
  }
}
